import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DOMPurify from "dompurify";

/**
 * Markdown — sanitised renderer.
 *
 * Phase B3. Used by KB articles and support-ticket message bodies. We feed
 * raw markdown through `remark-gfm` (tables, strikethrough, task lists) but
 * sanitise the eventual DOM via DOMPurify so user-submitted markdown can't
 * smuggle inline event handlers or <script> tags.
 *
 * The two-pass approach (markdown → HTML → DOMPurify clean) is overkill for
 * super-admin-authored KB but mandatory for the ticket-message surface where
 * customers can post arbitrary markdown including raw HTML escapes.
 */
const SANITIZE_CFG = {
  ALLOWED_TAGS: [
    "a", "p", "strong", "em", "del", "ul", "ol", "li", "h1", "h2", "h3",
    "h4", "h5", "h6", "blockquote", "code", "pre", "br", "hr",
    "table", "thead", "tbody", "tr", "td", "th",
    "img", "input",
  ],
  ALLOWED_ATTR: ["href", "title", "alt", "src", "type", "checked", "disabled"],
  ALLOW_DATA_ATTR: false,
};

export default function Markdown({ text, className = "" }) {
  if (!text) return null;
  return (
    <div
      className={`eb-markdown text-sm leading-relaxed text-navy-700 dark:text-navy-100 ${className}`}
      data-testid="markdown-body"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // External links open in a new tab; sanitise href via DOMPurify cfg above.
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer" className="text-orange-600 underline hover:text-orange-700" />
          ),
          h1: ({ node, ...props }) => <h1 className="text-xl font-semibold mt-4 mb-2" {...props} />,
          h2: ({ node, ...props }) => <h2 className="text-lg font-semibold mt-4 mb-2" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-base font-semibold mt-3 mb-1.5" {...props} />,
          code: ({ inline, ...props }) =>
            inline ? <code className="px-1 py-0.5 rounded bg-navy-100 dark:bg-navy-800 text-[12px] font-mono" {...props} />
                   : <code className="block p-3 rounded bg-navy-100 dark:bg-navy-800 text-[12px] font-mono overflow-x-auto" {...props} />,
          pre: ({ node, ...props }) => <pre className="my-3 rounded overflow-x-auto" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc ml-6 my-2" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal ml-6 my-2" {...props} />,
          li: ({ node, ...props }) => <li className="my-1" {...props} />,
          table: ({ node, ...props }) => <table className="my-3 text-xs border border-navy-200 rounded" {...props} />,
          th: ({ node, ...props }) => <th className="px-2 py-1 border-b border-navy-200 bg-navy-50 text-left font-semibold" {...props} />,
          td: ({ node, ...props }) => <td className="px-2 py-1 border-b border-navy-100" {...props} />,
          blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-4 border-orange-300 pl-3 my-3 text-navy-600 italic" {...props} />
          ),
        }}
      >
        {DOMPurify.sanitize(text, SANITIZE_CFG)}
      </ReactMarkdown>
    </div>
  );
}
